import { useValidatedForm, validateEmail } from '@words-happen/screens';
import {
  type LexiMood,
  SignInPrompt,
  WordsHappenButton,
  WordsHappenFormAlert,
  WordsHappenTextField,
} from '@words-happen/ui-components';
import type React from 'react';
import { useEffect, useId, useRef } from 'react';
import type { SignInLinkFailure } from '../../api';
import {
  MessageScreen,
  messageScreenActionsClassName,
  messageScreenFormClassName,
  messageScreenStatusClassName,
} from '../../components';
import type { Resend } from '../use-resend';

/**
 * What SignInLinkScreen shows: the link being checked or used, the address form (another device, or the wrong
 * address), why the link can't be used, or that it couldn't be checked.
 */
export type SignInLinkState = 'checking' | 'email' | Exclude<SignInLinkFailure, 'wrong-email'> | 'failed';

export interface SignInLinkScreenProps {
  state: SignInLinkState;
  /** The address the link is being used with, when known: prefills the form and is where a new link goes. */
  email?: string;
  /** Signing in with the typed address: the submit button shows its spinner. */
  submitting?: boolean;
  /** A form-level error, or why the link couldn't be checked (`failed`). Shown in a WordsHappenFormAlert. */
  error?: string;
  /** An address the link wasn't sent to, shown on the field until the next submit; it gets focus when it appears. */
  emailError?: string;
  /** "Email me a new link", for an expired or invalid link: its progress, cooldown and error (useResend). */
  newLink: Pick<Resend, 'sending' | 'cooldown' | 'error'>;
  /** Called with the trimmed address once it is well formed. */
  onSubmitEmail: (email: string) => void;
  /** "Try again": checks the link again, for `failed`. */
  onRetry: () => void;
  /** "Email me a new link". */
  onRequestNewLink: () => void;
  /** "Back to sign in". */
  onSignIn: () => void;
}

interface Copy {
  title: string;
  mood: LexiMood;
  body: string;
}

const COPY: Readonly<Record<Exclude<SignInLinkState, 'email'>, Copy>> = {
  checking: { title: 'Checking your link…', mood: 'focused', body: 'One moment.' },
  expired: {
    title: 'This link has expired',
    mood: 'surprised',
    body: 'Sign-in links only work for a limited time. We can send you a new one.',
  },
  invalid: {
    title: "This link doesn't work",
    mood: 'surprised',
    body: 'It may be incomplete, out of date or already used. We can send you a new one.',
  },
  'not-allowed': {
    title: "This account can't sign in yet",
    mood: 'surprised',
    body: "Words Happen isn't open yet.",
  },
  failed: { title: "We couldn't check your link", mood: 'surprised', body: 'Your link may still be good.' },
};

const VALIDATORS = { email: validateEmail };

/** Asks for the address the link was sent to, as Firebase requires when it wasn't requested on this device. */
const EmailForm: React.FC<
  Pick<SignInLinkScreenProps, 'email' | 'submitting' | 'error' | 'emailError' | 'onSubmitEmail'>
> = ({ email = '', submitting = false, error, emailError, onSubmitEmail }) => {
  const { values, errors, change, blur, validateAll } = useValidatedForm({ email }, VALIDATORS);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (emailError) {
      emailRef.current?.focus();
    }
  }, [emailError]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    if (validateAll()) {
      emailRef.current?.focus();
      return;
    }
    onSubmitEmail(values.email.trim());
  };

  return (
    <form className={messageScreenFormClassName} aria-label="Confirm your email" noValidate onSubmit={handleSubmit}>
      <WordsHappenTextField
        ref={emailRef}
        label="Email"
        name="email"
        type="email"
        autoComplete="username"
        autoCapitalize="none"
        spellCheck={false}
        value={values.email}
        error={errors.email ?? emailError}
        onChange={change('email')}
        onBlur={blur('email')}
      />

      {error ? <WordsHappenFormAlert>{error}</WordsHappenFormAlert> : null}

      <WordsHappenButton type="submit" variant="coral" size="lg" loading={submitting}>
        Sign in
      </WordsHappenButton>
    </form>
  );
};

/** "Email me a new link", with its cooldown and any error, for a link that can't be used. */
const NewLinkActions: React.FC<Pick<SignInLinkScreenProps, 'newLink' | 'onRequestNewLink' | 'onSignIn'>> = ({
  newLink,
  onRequestNewLink,
  onSignIn,
}) => {
  const cooldownId = useId();

  return (
    <>
      {newLink.error ? <WordsHappenFormAlert>{newLink.error}</WordsHappenFormAlert> : null}
      <WordsHappenButton
        variant="coral"
        size="lg"
        loading={newLink.sending}
        disabled={newLink.cooldown > 0}
        aria-describedby={newLink.cooldown > 0 ? cooldownId : undefined}
        onClick={onRequestNewLink}
      >
        Email me a new link
      </WordsHappenButton>
      {newLink.cooldown > 0 ? (
        <p id={cooldownId} className={messageScreenStatusClassName}>
          You can try again in {newLink.cooldown}s
        </p>
      ) : null}
      <SignInPrompt prefix={null} action="Back to sign in" onAction={onSignIn} />
    </>
  );
};

/**
 * /sign-in/link: a sign-in link being checked, the address form for a link opened on another device, or why the
 * link can't be used. An expired or invalid link offers a new one; a link that couldn't be checked offers to try
 * again.
 */
export const SignInLinkScreen: React.FC<SignInLinkScreenProps> = ({
  state,
  newLink,
  onRetry,
  onRequestNewLink,
  onSignIn,
  ...form
}) => {
  if (state === 'email') {
    return (
      <MessageScreen
        title="Confirm your email"
        mood="focused"
        action={
          <div className={messageScreenActionsClassName}>
            <EmailForm {...form} />
            <SignInPrompt prefix={null} action="Back to sign in" onAction={onSignIn} />
          </div>
        }
      >
        <p>This link was opened on a different device or browser. Enter the email address you asked for it with.</p>
      </MessageScreen>
    );
  }

  const { title, mood, body } = COPY[state];

  const action = () => {
    switch (state) {
      case 'checking':
        return null;
      case 'failed':
        return (
          <>
            {form.error ? <WordsHappenFormAlert>{form.error}</WordsHappenFormAlert> : null}
            <WordsHappenButton variant="coral" size="lg" onClick={onRetry}>
              Try again
            </WordsHappenButton>
            <SignInPrompt prefix={null} action="Back to sign in" onAction={onSignIn} />
          </>
        );
      case 'not-allowed':
        return <SignInPrompt prefix={null} action="Back to sign in" onAction={onSignIn} />;
      default:
        return <NewLinkActions newLink={newLink} onRequestNewLink={onRequestNewLink} onSignIn={onSignIn} />;
    }
  };

  return (
    <MessageScreen title={title} mood={mood} action={<div className={messageScreenActionsClassName}>{action()}</div>}>
      <p role={state === 'checking' ? 'status' : undefined}>{body}</p>
    </MessageScreen>
  );
};
